import { ActionIconButton } from '@/shared/components/ui/ActionIconButton';
import { Button } from '@/shared/components/ui/Button';
import { useClientPagination } from '@/shared/hooks/useClientPagination';
import { useEscapeClose } from '@/shared/hooks/useEscapeClose';
import { confirm } from '@/shared/lib/confirm';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Pencil, Plus, Trash2, X } from 'lucide-react';
import { useState } from 'react';
import { type HolidayRow, createHoliday, deleteHoliday, listHolidays, updateHoliday } from '../api';

/** 'YYYY-MM-DD' → 'DD/MM/YYYY' sem depender de fuso (não usa new Date). */
function formatBrDate(iso: string): string {
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return iso;
  return `${d}/${m}/${y}`;
}

/** Hoje no formato ISO 'YYYY-MM-DD' (fuso local do navegador — só p/ dimmar passados). */
function todayIso(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function FeriadosTab() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['holidays'], queryFn: listHolidays });
  const [editing, setEditing] = useState<HolidayRow | 'new' | null>(null);
  const all = q.data ?? [];
  const { visible, hasMore, sentinelRef } = useClientPagination(all);
  const today = todayIso();

  const delMut = useMutation({
    mutationFn: deleteHoliday,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['holidays'] }),
  });

  return (
    <section className="flex h-full flex-col overflow-hidden">
      <header className="mb-5 flex flex-shrink-0 items-center justify-between">
        <div>
          <h2
            className="font-bold text-[20px] text-n-900"
            style={{ letterSpacing: '-.02em', marginBottom: '4px' }}
          >
            Feriados
          </h2>
          <p className="text-[13px] text-n-500">
            No dia marcado, o chatbot é desligado e o cliente recebe a mensagem definida — o
            atendimento cai em pendentes no departamento padrão do canal.
          </p>
        </div>
        <Button onClick={() => setEditing('new')}>
          <Plus aria-hidden="true" />
          Novo feriado
        </Button>
      </header>

      {!q.isLoading && all.length > 0 && (
        <p className="mb-2 flex-shrink-0 text-[12px] text-n-500">
          {visible.length === all.length
            ? `${all.length} ${all.length === 1 ? 'feriado' : 'feriados'}`
            : `Mostrando ${visible.length} de ${all.length} feriados`}
        </p>
      )}
      <div className="min-h-0 flex flex-col overflow-y-auto rounded-md border border-n-200 bg-n-0 shadow-sm">
        <table className="w-full text-[12px]">
          <thead className="sticky top-0 z-10 border-b border-n-200 bg-n-50 text-left">
            <tr>
              <th className="px-[14px] py-[10px] text-[11px] font-bold uppercase tracking-[.05em] text-n-500 whitespace-nowrap">
                Nome
              </th>
              <th className="w-[120px] px-[14px] py-[10px] text-[11px] font-bold uppercase tracking-[.05em] text-n-500 whitespace-nowrap">
                Data
              </th>
              <th className="px-[14px] py-[10px] text-[11px] font-bold uppercase tracking-[.05em] text-n-500 whitespace-nowrap">
                Mensagem
              </th>
              <th className="w-[120px] px-[14px] py-[10px] text-right text-[11px] font-bold uppercase tracking-[.05em] text-n-500 whitespace-nowrap">
                Ações
              </th>
            </tr>
          </thead>
          <tbody>
            {q.isLoading && (
              <tr>
                <td colSpan={4} className="px-3 py-8 text-center">
                  <Loader2 size={18} className="mx-auto animate-spin text-n-400" />
                </td>
              </tr>
            )}
            {!q.isLoading && all.length === 0 && (
              <tr>
                <td colSpan={4} className="px-3 py-12 text-center text-n-500">
                  Nenhum feriado cadastrado.
                </td>
              </tr>
            )}
            {visible.map((r) => {
              const past = r.holidayDate < today;
              return (
                <tr
                  key={r.id}
                  className={`border-b border-n-100 last:border-b-0 hover:bg-n-50 ${past ? 'opacity-55' : ''}`}
                >
                  <td className="px-[14px] py-[11px] font-medium text-n-900">{r.name}</td>
                  <td className="px-[14px] py-[11px] whitespace-nowrap text-n-700">
                    {formatBrDate(r.holidayDate)}
                    {past && <span className="ml-1.5 text-[10px] text-n-400">(passado)</span>}
                  </td>
                  <td
                    className="max-w-0 truncate px-[14px] py-[11px] text-n-600"
                    title={r.message}
                  >
                    {r.message}
                  </td>
                  <td className="px-[14px] py-[11px]">
                    <div className="flex justify-end gap-1">
                      <ActionIconButton
                        icon={Pencil}
                        label="Editar"
                        tone="brand"
                        onClick={() => setEditing(r)}
                      />
                      <ActionIconButton
                        icon={Trash2}
                        label="Apagar"
                        tone="danger"
                        onClick={async () => {
                          const ok = await confirm({
                            title: `Apagar "${r.name}"?`,
                            confirmText: 'Apagar',
                            variant: 'danger',
                          });
                          if (ok) delMut.mutate(r.id);
                        }}
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
            {hasMore && (
              <tr ref={sentinelRef}>
                <td colSpan={4} className="py-4 text-center text-[12px] text-n-500">
                  <span className="inline-flex items-center gap-2">
                    <Loader2 size={14} className="animate-spin" /> Carregando mais…
                  </span>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <HolidayFormModal
          holiday={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </section>
  );
}

function HolidayFormModal({
  holiday,
  onClose,
}: {
  holiday: HolidayRow | null;
  onClose: () => void;
}) {
  useEscapeClose(true, onClose);
  const qc = useQueryClient();
  const [name, setName] = useState(holiday?.name ?? '');
  const [date, setDate] = useState(holiday?.holidayDate ?? '');
  const [message, setMessage] = useState(holiday?.message ?? '');
  const mut = useMutation({
    mutationFn: async () => {
      if (holiday) await updateHoliday(holiday.id, { name, date, message });
      else await createHoliday({ name, date, message });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['holidays'] });
      onClose();
    },
  });
  const valid = name.trim().length > 0 && /^\d{4}-\d{2}-\d{2}$/.test(date) && message.trim().length > 0;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-[400px] rounded-lg bg-n-0 p-6 shadow-lg">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-[16px]">{holiday ? 'Editar feriado' : 'Novo feriado'}</h3>
          <button type="button" onClick={onClose} className="text-n-500" aria-label="Fechar">
            <X size={16} />
          </button>
        </div>
        <div className="mt-4 flex flex-col gap-3">
          <label className="flex flex-col gap-1">
            <span className="font-semibold text-[12px] text-n-700">Nome</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex: Natal"
              className="rounded-md border border-n-200 px-3 py-2 text-[13px]"
              autoFocus
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="font-semibold text-[12px] text-n-700">Data</span>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="rounded-md border border-n-200 px-3 py-2 text-[13px]"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="font-semibold text-[12px] text-n-700">Mensagem</span>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              placeholder="digite a mensagem que o cliente recebe no feriado..."
              className="resize-none rounded-md border border-n-200 px-3 py-2 text-[13px]"
            />
            <span className="text-[11px] text-n-500">
              Enviada uma vez por contato no dia. Aceita variáveis (ex:{' '}
              <code className="text-n-600">{'{{firstname}}'}</code>).
            </span>
          </label>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={() => mut.mutate()} disabled={mut.isPending || !valid}>
            {mut.isPending && <Loader2 size={12} className="animate-spin" />}
            Salvar
          </Button>
        </div>
      </div>
    </div>
  );
}
